import { ReactNode, useState, useCallback } from "react";
import { Box, Typography } from "@mui/joy";
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle,
    Info,
    type LucideIcon,
} from "lucide-react";
import {
    NotificationContext,
    Notification,
    NotificationType,
} from "../context/NotificationContext";

interface NotificationProviderProps {
    children: ReactNode;
}

const NOTIFICATION_CONFIG: Record<
    NotificationType,
    { icon: LucideIcon; color: string }
> = {
    success: { icon: CheckCircle, color: "success.600" },
    danger: { icon: AlertCircle, color: "danger.600" },
    warning: { icon: AlertTriangle, color: "warning.600" },
    neutral: { icon: Info, color: "neutral.600" },
};

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const showNotification = useCallback(
        (message: string, type: NotificationType = "neutral", duration: number = 3000) => {
            const id = `notification-${Date.now()}-${Math.random()}`;
            const notification: Notification = { id, message, type, duration };

            setNotifications((prev) => [...prev, notification]);

            setTimeout(() => {
                setNotifications((prev) => prev.filter((n) => n.id !== id));
            }, duration);
        },
        [],
    );

    const showSuccess = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "success", duration);
        },
        [showNotification],
    );

    const showError = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "danger", duration);
        },
        [showNotification],
    );

    const showWarning = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "warning", duration);
        },
        [showNotification],
    );

    const showInfo = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "neutral", duration);
        },
        [showNotification],
    );

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const contextValue = {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    zIndex: 10001,
                    display: "flex",
                    flexDirection: "column-reverse",
                    gap: 1,
                    maxWidth: 420,
                    pointerEvents: "none",
                }}
            >
                {notifications.map((notification) => {
                    const config = NOTIFICATION_CONFIG[notification.type];
                    const Icon = config.icon;

                    return (
                        <Box
                            key={notification.id}
                            role="status"
                            onClick={() => removeNotification(notification.id)}
                            sx={{
                                pointerEvents: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.5,
                                py: 1,
                                minWidth: 280,
                                borderRadius: "4px",
                                border: "1px solid",
                                borderColor: "rgba(128, 128, 128, 0.2)",
                                bgcolor: "background.surface",
                                backdropFilter: "blur(12px)",
                                boxShadow: "sm",
                            }}
                        >
                            <Box
                                sx={{
                                    color: config.color,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Icon size={16} strokeWidth={2.25} />
                            </Box>
                            <Typography
                                level="body-sm"
                                sx={{
                                    color: config.color,
                                    fontWeight: 600,
                                }}
                            >
                                {notification.message}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </NotificationContext.Provider>
    );
};
